/**
 * formats.js — the team's report templates, as data (ADR 0016).
 *
 * A FORMAT is one of the team's GIMP templates. The operator picks the format
 * first; that template's own pages are listed in the template's order; each page
 * is built on its own and the chosen pages are stitched into one report. The
 * title, footer and version come from the template.
 *
 * Nothing here is code for a template. A new template, or a changed one, is an
 * edit to this file. Every value names where it came from:
 *
 *   source   the .xcf file and its md5 (tools/xcf-geometry/catalogue/SOURCES.md5)
 *   from     the template layer group a text was read from
 *   v        a page's layer group in the template, verbatim — its citation
 *   note     why a value is NOT read off the template
 *
 * How a page is built:
 *   use: '<recipe id>'   an existing recipe fills it (draft.js, fibulaRecipes.js);
 *                        'set/id' names a recipe of another set
 *   use: 'g-…'           a generic page (genericPages.js) with the template's
 *                        caption presets; the operator picks the meshes
 *   hand: '<why>'        listed, made in GIMP — the Builder cannot draw it
 *
 * Caption presets are the template's own text, never a side, a measurement or a
 * placeholder: "Left fibula was used…" is derived by the recipes that can see the
 * files, and "Fixation hole diameter = mm" is typed per case. formats.test.js
 * enforces that.
 *
 * The page lists were read from the committed layer dumps
 * (tools/xcf-geometry/fixtures/<slug>/dump.json), each page's layout from its
 * MEASURED grid (2 × 3 cells = six views, 1 × 1 = single view), not from layer
 * names. Templates that do not number their pages keep the layer-stack order;
 * the team confirms that order.
 */
import { CHASSIS } from './template.js'

const SIX = 'g-six-views'
const ONE = 'g-single-view'
const ROW = 'g-view-row'
const PRINT = 'g-print-models'
const CT = 'g-ct-data'

const LEGAL_PENDING = 'this template carries its own agreement / terms wording — made in GIMP until the team sends the exact text'
const MEASURED = 'dimensions are drawn by hand; no mesh carries them'
const COLOUR_MAP = 'the colour map is made in ZBrush; no mesh carries it'
// 🔴 `omit` keeps the template page on the RECORD while leaving it out of the
// report. Deepak, 2026-09-25, on the orthognathic feedback: "exclude the
// agreement page and terms and condition pages (last 2 pages)".
//
// Deleting the three entries would have been simpler and wrong. The template
// carries 17 pages and the catalogue says so; a format that claimed 14 would
// contradict its own citation, and `v:` on each entry is that citation. It also
// matters that the template's OWN page-number layer reads "Page 09 of 14" —
// 17 less these three — so the template already agrees with the team, and the
// record has to be able to show both numbers.
//
// Per format, not per house: the fibula report still builds its own agreement
// and terms pages, and six other templates list theirs as LEGAL_PENDING.
const TEAM_ENCLOSES = 'the signed agreement and the terms pages are enclosed by the team, not generated'

export const FORMATS = [
  // ── case reports ───────────────────────────────────────────────────────────
  {
    id: 'orthognathic',
    slug: 'reference-orthognathic-case-report-template',
    group: 'case-report',
    label: 'Orthognathic case report',
    source: { file: 'Reference Orthognathic Case Report Template - O3D-2026-XX-XX-00XX_x_V1.3.5.xcf', md5: '324d9242117c13345ff6e00185aacab0' },
    palette: 'orthognathic',
    recipes: 'orthognathic',
    legal: 'house',
    fileSuffix: 'case_report',
    chrome: {
      titleLine1: { text: 'Online Surgical Planning', from: 'Header for Images' },
      // The layer reads "Orthognathic Case  Report" (two spaces); one is printed,
      // as every delivered orthognathic report has.
      titleLine2: { text: 'Orthognathic Case Report', from: 'Header for Images' },
      productName: null,
      version: { text: 'V1.3.5', note: 'from the template file name' },
    },
    pages: [
      { key: 'agreement', v: 'Page 1 Agreeement', title: 'Agreement', use: 'agreement', omit: TEAM_ENCLOSES },
      { key: 'source-data', v: 'Page 2 CT Data Info', title: 'CT / source data', use: 'source-data' },
      { key: 'preop', v: 'Page 3 Native Skull', title: 'Native skull', use: 'preop' },
      { key: 'teeth-replaced', v: 'Page 4 Teeth replaced from the intraoral scan copy', title: 'Teeth replaced from the intraoral scan', use: SIX, tc: 'Native skull model with teeth replaced from the intraoral scan' },
      { key: 'steps', v: 'Page 5 Steps', title: 'Movement steps', hand: 'the step-by-step movement pages are drawn by hand', sheet: SIX },
      { key: 'teeth-interference', v: 'Page 6 Teeth Interference', title: 'Teeth interference', hand: COLOUR_MAP, sheet: SIX },
      { key: 'comparison', v: 'Page 7 Comparison Images - A, RL, LL', title: 'Comparison — A, RL, LL', use: 'comparison' },
      { key: 'comparison-psi', v: 'Page 8 Comparison Images - P, S, I', title: 'Comparison — P, S, I', use: 'comparison', views: ['P', 'S', 'I'] },
      { key: 'maxilla-splint-with-skull', v: 'Page 9 Splint with Skull copy', title: 'Maxilla movement splint with skull', use: SIX, tc: 'Maxilla movement splint with autorotated mandible' },
      { key: 'maxilla-splint', v: 'Page 10 Splint 6 view copy #1', title: 'Maxilla movement splint', use: SIX, tc: 'Maxilla movement splint' },
      { key: 'maxilla-splint-measurement', v: 'Page 11 Splint measurement', title: 'Maxilla splint thickness', hand: MEASURED, sheet: SIX },
      { key: 'mandible-splint-with-skull', v: 'Page 12 Mandible Splint with Skull', title: 'Mandible movement splint with skull', use: SIX, tc: 'Mandible movement splint with autorotated post plan mandible' },
      { key: 'mandible-splint', v: 'Page 13 Splint 6 view copy', title: 'Mandible movement splint', use: SIX, tc: 'Mandible movement splint' },
      { key: 'mandible-splint-measurement', v: 'Page 14 Splint measurement', title: 'Mandible splint thickness', hand: MEASURED, sheet: SIX },
      { key: 'print-models', v: 'Page 15 Print Models', title: 'Print models', use: 'print-models' },
      { key: 'terms-1', v: 'Page 16 T&C copy', title: 'Terms and Conditions (1 of 2)', use: 'terms-1', omit: TEAM_ENCLOSES },
      { key: 'terms-2', v: 'Page 17 T&C #1', title: 'Terms and Conditions (2 of 2)', use: 'terms-2', omit: TEAM_ENCLOSES },
    ],
    notPages: { 'Text for Reports copy #4': 'a hidden copy of the header fields' },
  },
  {
    id: 'fibula',
    slug: 'reference-case-report-of-free-fibula-flap',
    group: 'case-report',
    label: 'Free fibula flap case report',
    source: { file: 'Reference Case Report of Free Fibula Flap - O3D-2026-XX-XX-00XX_x_V1.9.3.xcf', md5: 'f0deb6d68c1d3699b1b7fe9100ba7808' },
    palette: 'fibula',
    recipes: 'fibula',
    legal: 'house',
    fileSuffix: 'case_report',
    chrome: {
      titleLine1: { text: 'Online Surgical Planning', from: 'Header for Images V1.8.1' },
      titleLine2: { text: 'Case Report of Mandibular Reconstruction with Free Fibula Flap', from: 'Header for Images V1.8.1' },
      productName: { text: 'Case Report of FFF', from: 'Footer' },
      version: { text: 'V1.9.3', from: 'Footer' },
    },
    pages: [
      { key: 'agreement', v: 'Page 1 Agreeement', title: 'Agreement', use: 'agreement' },
      { key: 'ct-data', v: 'Page 2 CT Data Info', title: 'CT / source data', use: CT },
      { key: 'native-skull', v: 'Page 3 Native Skull', title: 'Native skull', use: 'native-skull' },
      // A ROW, not a single view: the template's own page carries four visible view
      // badges over ONE frame, in the order A, LL, P, RL — the laterals sit between
      // the frontal views. Read as 1x1 it offered one view, so the operator added the
      // page four times and the leg took four sheets (the team, 1 Oct: "It should be
      // in one page").
      { key: 'native-leg', v: 'Page 4 Native Legs', title: 'Native leg', use: ROW, tc: 'Native leg', bc: 'Preoperative images' },
      { key: 'teeth-replaced', v: 'Page 5 Teeth replaced from the intraoral scan', title: 'Teeth replaced from the intraoral scan', use: SIX, tc: 'Native skull model with teeth replaced from the intraoral scan' },
      { key: 'repositioned-mandible', v: 'Page 6 Native Skull with Repositioned Mandible', title: 'Native skull with repositioned mandible', use: SIX, tc: 'Native skull with repositioned mandible' },
      { key: 'resection-area', v: 'Page 7 Resection Area', title: 'Resection area', use: 'resection-area' },
      { key: 'resection-nerve', v: 'Page 8 Resection Area With Nerve', title: 'Resection area with nerve', use: ONE, tc: 'Resection area' },
      { key: 'mandible-resection-guides', v: 'Page 9 Mandible Guides / Maxilla Guides', title: 'Mandible resection guides', use: 'mandible-resection-guides' },
      // Three views in one frame, measured: the reference's Page 10 shows A, LL and P
      // with the RL badge hidden beside a hidden "Right leg and right fibula" caption.
      // LAT is whichever lateral the harvested leg calls for.
      { key: 'leg-and-fibula', v: 'Page 10 Leg and Fibula', title: 'Leg and fibula', use: ROW, views: ['A', 'LAT', 'P'] },
      { key: 'leg-with-guide', v: 'Page 11 Leg with Guide', title: 'Leg with fibula guide', use: ROW, views: ['A', 'LAT', 'P'], tc: 'Fibula osteotomy guide' },
      // Four views in one frame, in the native-leg page's order. The template shows
      // ONE view on both pages; the team asked for four (19 Oct 2026: "four views:
      // Anterior, Left, Posterior, and Right").
      { key: 'fibula-and-guide', v: 'Page 12 Fibula Bone and Fibula Guide', title: 'Fibula and fibula guide', use: ROW, views: ['A', 'LL', 'P', 'RL'], tc: 'Fibula osteotomy guide' },
      { key: 'fibula-guide', v: 'Page 13 Only Fibula Guide', title: 'Fibula guide', use: ROW, views: ['A', 'LL', 'P', 'RL'], tc: 'Fibula osteotomy guide' },
      { key: 'linear-measurements', v: 'Page 14  Linear measurements', title: 'Straight line measurements', hand: MEASURED, sheet: ONE },
      { key: 'artery-measurements', v: 'Page 15 Artery Linear measurements', title: 'Artery measurements', hand: MEASURED, sheet: ONE },
      { key: 'reconstructed-mandible', v: 'Page 16 Reconstructed Mandible Color', title: 'Reconstructed mandible', use: 'reconstructed-mandible' },
      { key: 'reconstructed-single', v: 'Page 17 Reconstructed Mandible Color - Single view', title: 'Reconstructed mandible — single view', use: ONE, tc: 'Reconstructed mandible' },
      { key: 'implant-distances', v: 'Page 18 Ditance between Dental implants in segments copy', title: 'Distance between dental implants', hand: MEASURED, sheet: ONE },
      { key: 'reconstructed-plating', v: 'Page 19 Reconstructed Mandble plating', title: 'Reconstructed mandible — plating surface', use: SIX, tc: 'Reconstructed mandible' },
      { key: 'post-plan-skull', v: 'Page 20 Post Plan Skull', title: 'Post plan skull', use: 'post-plan-skull' },
      { key: 'print-models', v: 'Page 21 Print Models #2', title: 'Print models', use: PRINT },
      { key: 'terms-1', v: 'Page 24 T&C', title: 'Terms and Conditions (1 of 2)', use: 'terms-1' },
      { key: 'terms-2', v: 'Page 25 T&C #1', title: 'Terms and Conditions (2 of 2)', use: 'terms-2' },
    ],
    notPages: {
      'Page 22 Print Models #3': 'another print-models sheet — the Print models page adds sheets as needed',
      'Page 23 Print Models #1': 'another print-models sheet — the Print models page adds sheets as needed',
      'Text for Reports copy #4': 'a hidden copy of the header fields',
    },
  },
  {
    id: 'scapula',
    slug: 'reference-scapula-guide-case-report',
    group: 'case-report',
    label: 'Scapula guide case report',
    source: { file: 'Reference Scapula Guide Case Report - O3D-2026-XX-XX-00XX_x_V1.2.2.xcf', md5: 'aa4128808d1ccfc87ce52e1552598322' },
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours',
    recipes: null,
    legal: null,
    fileSuffix: 'case_report',
    chrome: {
      titleLine1: { text: 'Online Surgical Planning', from: 'Header for Images' },
      titleLine2: { text: 'Scapula Guide Case Report', from: 'Header for Images' },
      productName: { text: 'Scapula Guide Case Report', from: 'Footer #1' },
      version: { text: 'V1.2.2', from: 'Footer #1' },
    },
    pages: [
      { key: 'agreement', v: 'Page 1 Agreeemnt', title: 'Agreement', hand: LEGAL_PENDING },
      { key: 'ct-data', v: 'Page 2 CT Data Info', title: 'CT / source data', use: CT },
      { key: 'native-skull', v: 'Page 3 Native Skull', title: 'Native skull', use: SIX, tc: 'Native skull' },
      { key: 'native-scapula', v: 'Page 4 Native Scapula', title: 'Native scapula', use: SIX },
      { key: 'resection-area', v: 'Page 5 Resection Area', title: 'Resection area', use: SIX, tc: 'Resection area' },
      { key: 'resection-nerve', v: 'Page6  Resection Area With Nerve', title: 'Resection area with nerve', use: ONE, tc: 'Resection area' },
      { key: 'mandible-guides', v: 'Page 7 Mandible Guides / Maxilla Guides', title: 'Mandible resection guides', use: SIX },
      { key: 'scapula-segments', v: 'Page 8 Scapula resection', title: 'Scapula segments', use: SIX },
      { key: 'scapula-with-guide', v: 'Page 9 Scapula + Scapula guide', title: 'Scapula with scapula guide', use: SIX },
      { key: 'scapula-guide', v: 'Page 10 Scapula guide only', title: 'Scapula guide', use: ONE },
      { key: 'scapula-guide-single', v: 'Page 12 Single of Scapula + guide', title: 'Scapula and guide — single view', use: ONE },
      { key: 'measurements', v: 'Page 13 Measurement', title: 'Measurements', hand: MEASURED, sheet: ONE },
      { key: 'reconstructed-colour', v: 'Page 14 Reconstructed Mandible colour', title: 'Reconstructed mandible — colour', use: SIX },
      { key: 'reconstructed-colour-single', v: 'Page 15 Reconstructed Mandible colour - Single view', title: 'Reconstructed mandible — single view', use: ONE },
      { key: 'reconstructed-measurement', v: 'Page 16 Reconstructed Mandible colour - Measurement', title: 'Reconstructed mandible — measurements', hand: MEASURED, sheet: ONE },
      { key: 'scapula-plating', v: 'Page 17 Scapula Single view - plating surface', title: 'Scapula — plating surface', use: ONE },
      { key: 'reconstructed-plating', v: 'Page 18 Reconstructed Mandible - plating surface', title: 'Reconstructed mandible — plating surface', use: SIX, tc: 'Reconstructed mandible' },
      { key: 'reconstructed-plate', v: 'Page 19 Reconstructed Mandible + plate', title: 'Reconstructed mandible with plate', use: SIX, tc: 'Reconstructed mandible' },
      { key: 'plate-single', v: 'Page 20 Reconstruction plate - single', title: 'Plate screws and nerve', use: ONE, tc: 'Relationship of plate screws and inferior alveolar nerve' },
      { key: 'post-plan-skull', v: 'Page 21 Post Skull', title: 'Post plan skull', use: SIX, tc: 'Post plan skull' },
      { key: 'print-models', v: 'Page 22 Print Models', title: 'Print models', use: PRINT },
      { key: 'terms-1', v: 'Page 25 T&C #1', title: 'Terms and Conditions (1 of 2)', hand: LEGAL_PENDING },
      { key: 'terms-2', v: 'Page 26  T&C', title: 'Terms and Conditions (2 of 2)', hand: LEGAL_PENDING },
    ],
    notPages: {
      'Page 11 Scapula guide only': 'a second copy of the scapula guide page',
      'Page 23 Print Models': 'another print-models sheet — the Print models page adds sheets as needed',
      'Page 24 Print Models': 'another print-models sheet — the Print models page adds sheets as needed',
      'Text for Reports copy #4': 'a hidden copy of the header fields',
    },
  },
  {
    id: 'cranioplasty',
    slug: 'reference-craniotomy-case-report',
    group: 'case-report',
    label: 'Cranioplasty case report',
    source: { file: 'Reference Craniotomy Case Report - O3D-2026-XX-XX-00XX_x_V1.8.3.xcf', md5: '4f5835cdd7add0b4ec6990ca9e1d8a4d' },
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours (implant and mould parts)',
    recipes: null,
    legal: null,
    fileSuffix: 'case_report',
    chrome: {
      titleLine1: { text: 'Online Surgical Planning', from: 'Header' },
      titleLine2: { text: 'Cranioplasty Case Report', from: 'Header' },
      productName: { text: 'Cranioplasty Case Report', from: 'Footer' },
      version: { text: 'V1.8.3', from: 'Footer' },
    },
    pages: [
      { key: 'agreement', v: 'Page 1 Agreeemnt', title: 'Agreement', hand: LEGAL_PENDING },
      { key: 'ct-data', v: 'Page 2 CT Data Info', title: 'CT / source data', use: CT },
      { key: 'native-skull', v: 'Page 3 Native Skull', title: 'Native skull', use: SIX, tc: 'Native skull' },
      { key: 'pre-correction', v: 'Page 4 Resection Area', title: 'Pre-correction skull', use: SIX, tc: 'Pre-correction skull showing bony irregularities and loose fragments' },
      { key: 'bone-interference', v: 'Page 5 Bone Intereference', title: 'Bone interference', use: ONE, tc: 'Pre-correction anatomy showing bony irregularities and loose fragments' },
      { key: 'post-correction', v: 'Page 6 Modified Skull', title: 'Post-correction skull', use: SIX, tc: 'Post-correction skull after removal of bony irregularities and loose fragments' },
      { key: 'mock-implant', v: 'Page 7 Mock Implant', title: 'Simulated PMMA implant — single view', use: ONE, tc: 'Simulated PMMA implant' },
      { key: 'implant-on-skull-single', v: 'Page 8 Single view of Simulated Implant (White) + Modified Skull copy', title: 'Implant on post-correction skull — single view', use: ONE, tc: 'Simulated PMMA implant on post-correction skull' },
      { key: 'thickness-analysis', v: 'Page 9 Thickness Analysis', title: 'Implant thickness analysis', hand: COLOUR_MAP, sheet: ONE },
      { key: 'implant-colour', v: 'Page 10 Only Simulated Implant (color)', title: 'Simulated PMMA implant', use: SIX, tc: 'Simulated PMMA implant' },
      { key: 'implant-colour-on-skull', v: 'Page 11 Simulated Implant (Colour) + Modified Skull', title: 'Implant (colour) on post-correction skull', use: SIX, tc: 'Simulated PMMA implant on post-correction skull' },
      { key: 'implant-white-on-skull', v: 'Page 12 Simulated Implant (White) + Modified Skull', title: 'Implant (white) on post-correction skull', use: SIX, tc: 'Simulated PMMA implant on post-correction skull' },
      { key: 'mould-halves', v: 'Page 13 Mould Halves', title: 'Mould halves and implant', hand: 'several labelled renders on one page — made in GIMP', sheet: ONE },
      { key: 'print-models', v: 'Page 14 Print Models', title: 'Print models', use: PRINT },
      { key: 'volume-and-video', v: 'Page 15 QR', title: 'Implant volume and video', hand: 'the PMMA volume, the material and the video QR code are filled in by hand' },
      { key: 'terms-2', v: 'Page 16 T&C #1', title: 'Terms and Conditions (indemnification)', hand: LEGAL_PENDING },
      { key: 'terms-1', v: 'Page 17 T&C', title: 'Terms and Conditions', hand: LEGAL_PENDING },
    ],
    notPages: { 'Header Credentials': 'a hidden copy of the header fields' },
  },
  {
    id: 'ti-psi',
    slug: 'reference-ti-psi-case-report-2026',
    group: 'case-report',
    label: 'Titanium PSI case report',
    source: { file: 'Reference Ti PSI Case Report 2026 - O3D-2026-XX-XX-00XX_x_V1.3.1.xcf', md5: 'af21bb10b52c4e18766fcd581215116b' },
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours (implant parts)',
    recipes: null,
    // The 3 legal pages are GENERATED, from this template's own wording — not the house text,
    // which differs in more than the product name. Team, 10 Oct 2026: "Agreement page , terms
    // and conditions pages x 2 is not generated from the case report builder for Titanium PSI ."
    // They were `hand: LEGAL_PENDING` ("until the team sends the exact text"); the exact text was
    // in their template all along. See boilerplate.js TI_PSI_AGREEMENT_BODY.
    //
    // Page 16 reuses the house `g-terms-2`: the template's own page 16 is a raster and its words
    // cannot be read out of it (Deepak, 10 Oct, decided against OCR and against waiting).
    legal: null,
    fileSuffix: 'case_report',
    chrome: {
      titleLine1: { text: 'Online Surgical Planning', from: 'Header for Images New' },
      titleLine2: { text: 'Case Report for Titanium Patient Specific Implant', from: 'Header for Images New' },
      productName: { text: 'Ti PSI Case Report', from: 'Footer #1' },
      version: { text: 'V1.3.1', from: 'Footer #1' },
    },
    pages: [
      { key: 'agreement', v: 'Page 1 Agreeemnt copy', title: 'Agreement', use: 'g-ti-agreement' },
      { key: 'ct-data', v: 'Page 2 CT Data Info', title: 'CT / source data', use: CT },
      { key: 'native-skull', v: 'Page 3 Native Skull', title: 'Native skull', use: SIX, tc: 'Native skull', bc: 'Preoperative images' },
      { key: 'resection-area', v: 'Page 4 Resection Area with Native Skull', title: 'Resection area', use: SIX, tc: 'Resection area' },
      { key: 'marking-guides', v: 'Page 5 Resection Marking Guide', title: 'Resection area marking guides', use: SIX, tc: 'Resection area marking guides' },
      { key: 'marking-guide-single', v: 'Page 6 Resection area marking guide - Enlarged', title: 'Marking guide — enlarged', use: ONE, tc: 'Resection area marking guide' },
      { key: 'resected-skull', v: 'Page 7 Resected Skull', title: 'Resected skull', use: SIX, tc: 'Resected skull' },
      { key: 'mock-implant-on-skull', v: 'Page 8 Skull with Mock Implant ( Color )', title: 'Skull with mock implant', use: SIX, tc: 'Mock implant' },
      { key: 'mock-implants-single', v: 'Page 9 Mock Implants - Enlarged ( COlor )', title: 'Mock implants — enlarged', use: ONE, tc: 'Mock implants' },
      { key: 'titanium-on-bone', v: 'Page 12 Mock Implant with Bone - Titanium Color', title: 'Titanium PSI on bone', use: SIX, tc: 'Titanium PSI' },
      { key: 'titanium', v: 'Page 13 Mock Implant - Titanium Color', title: 'Titanium PSI', use: SIX, tc: 'Titanium PSI' },
      { key: 'measurements', v: 'Page 14 Measurements', title: 'Measurements', hand: MEASURED, sheet: ONE },
      { key: 'print-models', v: 'Page 15 Print Models', title: 'Print models', use: PRINT },
      { key: 'terms-2', v: 'Page 16 T&C', title: 'Terms and Conditions (indemnification)', use: 'g-terms-2' },
      { key: 'terms-1', v: 'Page 17 T&C copy', title: 'Terms and Conditions', use: 'g-ti-terms-1' },
    ],
    notPages: {
      'Page 10 Mock Implant - Enlarged ( Color ) #1': 'another enlarged mock-implant view — add that page again',
      'Page 11 Mock Implant - Enlarged ( Color )': 'another enlarged mock-implant view — add that page again',
      'Credrntials of Hosptal and Dr name': 'a hidden copy of the header fields',
      'Layer Group #1': 'the box contents slip, not a report page',
    },
  },
  {
    id: 'general',
    slug: 'reference-general-case-report',
    group: 'case-report',
    label: 'General case report',
    source: { file: 'Reference General Case Report - O3D-2026-XX-XX-00XX_x_V1.2.2.xcf', md5: '8fcb36a4bbe1c9e3e6d08e74b964d66f' },
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours',
    recipes: null,
    legal: null,
    fileSuffix: 'case_report',
    chrome: {
      titleLine1: { text: 'Online Surgical Planning', from: 'Header for Images' },
      titleLine2: { text: 'Case Report', from: 'Header for Images' },
      productName: { text: 'Case Report', from: 'Footer #1' },
      version: { text: 'V1.2.2', from: 'Footer #1' },
    },
    pages: [
      { key: 'agreement', v: 'Page 1 Agreeemnt', title: 'Agreement', hand: LEGAL_PENDING },
      { key: 'ct-data', v: 'Page 2 CT Data Info', title: 'CT / source data', use: CT },
      { key: 'native-skull', v: 'Page 3 Native Skull', title: 'Native skull', use: SIX, tc: 'Native skull', bc: 'Preoperative images' },
      { key: 'resection-area', v: 'Page 4 Resection Area', title: 'Resection area', use: SIX, tc: 'Resection area' },
      { key: 'resection-nerve', v: 'Page 5 Resection Area With Nerve', title: 'Resection area with nerve', use: ONE, tc: 'Resection area' },
      { key: 'mandible-guides', v: 'Page 6 Mandible Guides / Maxilla Guides', title: 'Mandible resection guides', use: SIX },
      { key: 'measurements', v: 'Page 7 Measurement', title: 'Measurements', hand: MEASURED, sheet: ONE },
      { key: 'print-models', v: 'Page 8 Print Models', title: 'Print models', use: PRINT },
      { key: 'terms-1', v: 'Page 9 T&C', title: 'Terms and Conditions (1 of 2)', hand: LEGAL_PENDING },
      { key: 'terms-2', v: 'Page 10 T&C', title: 'Terms and Conditions (2 of 2)', hand: LEGAL_PENDING },
    ],
    notPages: { 'Text for Reports copy #4': 'a hidden copy of the header fields' },
  },

  // ── images for the surgeon ─────────────────────────────────────────────────
  {
    id: 'preview',
    slug: 'preview-images',
    group: 'images',
    label: 'Preview images',
    source: { file: 'Preview Images - O3D-2026-XX-XX-00XX_x_V1.4.3.xcf', md5: '6656e92c0143dc6711edbe4b6413ba03' },
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours',
    recipes: null,
    legal: null,
    fileSuffix: 'preview_images',
    chrome: {
      titleLine1: { text: '', note: 'this template has one header line' },
      titleLine2: { text: 'Preview Images', from: 'Header Credentials' },
      productName: { text: 'Preview Images', from: 'Footer' },
      version: { text: 'V1.4.3', from: 'Footer' },
    },
    pages: [
      { key: 'skull', v: 'Skull Single View Images', title: 'Skull — single view', use: ONE },
      { key: 'leg', v: 'Right Leg Single View Images', title: 'Leg — single view', use: ONE },
      { key: 'ct-data', v: 'CT Data Info', title: 'CT / source data', use: CT },
    ],
    notPages: {
      'Page 1 Case ID & Date': 'the case ID and date strip at the foot of a page',
      'Left Leg Single View Images': 'the same page for the other leg — add Leg single view again',
    },
  },
  {
    id: 'planning',
    slug: 'planning-images',
    group: 'images',
    label: 'Planning images',
    source: { file: 'Planning Images - O3D-2026-XX-XX-00XX_x_V1.4.4.xcf', md5: '8dcc38a1dd0920685fac78638941ad36' },
    // This template's header title sits inside each page group, so the
    // extractor records no chrome title for it and the size has to be stated:
    // "Planning Images", 14.4 pt, measured on its own page layers.
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours',
    recipes: null,
    legal: null,
    fileSuffix: 'planning_images',
    chrome: {
      titleLine1: { text: '', note: 'this template has one header line' },
      titleLine2: { text: 'Planning Images', from: 'Native Skull' },
      productName: null,
      version: { text: 'V1.4.4', note: 'from the template file name' },
    },
    pages: [
      { key: 'ct-data', v: 'Page 1 CT Data Info', title: 'CT / source data', use: CT, header: 'Preview Image' },
      { key: 'native-skull', v: 'Native Skull', title: 'Native skull', use: SIX, tc: 'Native skull' },
      { key: 'native-leg', v: 'Native Leg', title: 'Native leg', use: ROW, tc: 'Native leg' },
      { key: 'repositioned-mandible', v: 'Repo Mandible (if done)', title: 'Repositioned mandible', use: SIX, tc: 'Repositioned mandible' },
      { key: 'resection-area', v: 'Resection Area', title: 'Resection area', use: SIX, tc: 'Resection area' },
      { key: 'resection-nerve', v: 'Resection Area - Nerve Traced', title: 'Resection area — nerve traced', use: ONE, tc: 'Resection area' },
      { key: 'reconstructed-plating', v: 'Reconstructed Mandible - with plating surface highlighted', title: 'Reconstructed mandible — plating surface', use: SIX },
      { key: 'artery-measurements', v: 'Artery Linear measurements', title: 'Artery measurements', hand: MEASURED, sheet: ONE },
      { key: 'reconstructed-colour', v: 'Reconstructed Mandible - colour', title: 'Reconstructed mandible — colour', use: SIX },
      { key: 'reconstructed-single', v: 'Reconstructed Mandible -  Single View', title: 'Reconstructed mandible — single view', use: ONE },
      { key: 'post-plan-skull', v: 'Post Plan Skull', title: 'Post plan skull', use: SIX, tc: 'Post plan skull' },
      { key: 'comparison', v: 'Page  Comparison Images - A, RL, LL', title: 'Comparison — A, RL, LL', use: 'orthognathic/comparison', header: 'Comparison Images', leftCaption: 'Native skull', rightCaption: 'Post plan skull', bottomCaption: '' },
      { key: 'comparison-psi', v: 'Page  Comparison Images - P, S, I', title: 'Comparison — P, S, I', use: 'orthognathic/comparison', header: 'Comparison Images', leftCaption: 'Native skull', rightCaption: 'Post plan skull', bottomCaption: '', views: ['P', 'S', 'I'] },
    ],
    notPages: {},
  },
  {
    id: 'design-confirmation',
    slug: 'images-for-design-confirmation',
    group: 'images',
    label: 'Images for design confirmation',
    source: { file: 'Images for Design Confirmation - O3D-2026-XX-XX-00XX_x_V1.3.4.xcf', md5: '69841859090fd1946b7fee51623e4ecc' },
    // Measured against this template and verified: ORBITAL_GEOMETRY (ADR 0012).
    palette: 'orbital',
    // The orbital pages (volumes, difference model) are built but held until the
    // design team confirms the orbital file names.
    recipes: 'orbital',
    held: 'Waiting for the design team to confirm the orbital file names.',
    legal: null,
    fileSuffix: 'design_confirmation',
    chrome: {
      titleLine1: { text: '', note: 'this template has one header line' },
      titleLine2: { text: 'Images for Design Confirmation', from: 'Native Skull' },
      productName: null,
      version: { text: 'V1.3.4', note: 'from the template file name' },
    },
    pages: [
      // These two are the orbital recipes: measured against THIS template and
      // verified against the delivered report (ADR 0009). They fill themselves.
      { key: 'preview', v: 'Page 1 CT Data Info', title: 'CT / source data', use: 'preview' },
      { key: 'native-skull', v: 'Native Skull', title: 'Native skull', use: 'native-skull' },
      { key: 'pre-correction', v: 'Resection Area', title: 'Pre-correction skull', use: SIX, tc: 'Pre-correction skull showing bony irregularities and loose fragments' },
      { key: 'pre-correction-single', v: 'Resection Area - SV', title: 'Pre-correction anatomy — single view', use: ONE, tc: 'Pre-correction anatomy showing bony irregularities and loose fragments' },
      { key: 'post-correction', v: 'Modified Skull', title: 'Post-correction skull', use: SIX, tc: 'Post-correction skull after removal of bony irregularities and loose fragments' },
      { key: 'thickness-analysis', v: 'Simulated PMMA implant thickness analysis', title: 'Implant thickness analysis', hand: COLOUR_MAP, sheet: ONE },
      { key: 'implant-on-skull-single', v: 'Flap on Skull 1mm gap', title: 'Implant on post-correction skull — single view', use: ONE, tc: 'Simulated PMMA implant on post-correction skull' },
      { key: 'implant', v: 'Simulated PMMA implant #1', title: 'Simulated PMMA implant', use: SIX, tc: 'Simulated PMMA implant' },
      { key: 'implant-on-skull', v: 'Flap on Skull', title: 'Implant on post-correction skull', use: SIX, tc: 'Simulated PMMA implant on post-correction skull' },
    ],
    notPages: {},
  },
  {
    id: 'print-confirmation',
    slug: 'images-for-print-confirmation',
    group: 'images',
    label: 'Images for print confirmation',
    source: { file: 'Images for Print Confirmation - O3D-2026-XX-XX-00XX_x_V1.3.3.xcf', md5: '9a9be2847d975ed1f61c746147d42c5e' },
    // As for planning images: the header title is inside each page group.
    // "Images for Print Confirmation", 14.4 pt.
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours',
    recipes: null,
    legal: null,
    fileSuffix: 'print_confirmation',
    chrome: {
      titleLine1: { text: '', note: 'this template has one header line' },
      titleLine2: { text: 'Images for Print Confirmation', from: 'Page 1  Print Model' },
      productName: null,
      version: { text: 'V1.3.3', note: 'from the template file name' },
    },
    pages: [
      { key: 'print-model', v: 'Page 1  Print Model', title: 'Print model — six views', use: SIX },
      { key: 'print-model-single', v: 'Page 2  Print Model - Single View', title: 'Print model — single view', use: ONE },
      { key: 'ct-data', v: 'Page 3 CT Data Info', title: 'CT / source data', use: CT, header: 'Preview Image' },
    ],
    notPages: {},
  },

  // ── other ──────────────────────────────────────────────────────────────────
  {
    id: 'helmet',
    slug: 'instructions-for-cranial-helmet',
    group: 'other',
    label: 'Cranial helmet instructions',
    source: { file: 'Instructions for Cranial Helmet  - O3D-2026-MM-DD-00XX_y_V1.1.1.xcf', md5: 'f45767ade6aaf7d9f1f9ab8e468152ad' },
    palette: 'fibula',
    paletteNote: 'first pass — the team confirms the colours',
    recipes: null,
    legal: null,
    fileSuffix: 'helmet_instructions',
    chrome: {
      titleLine1: { text: '', note: 'this template has no report title; each page carries its own heading' },
      titleLine2: { text: '', note: 'this template has no report title; each page carries its own heading' },
      productName: { text: 'Instruction', from: 'Footer #1' },
      version: { text: 'V1.1.1', from: 'Footer #1' },
    },
    pages: [
      { key: 'helmet-parts', v: 'Page 1 - Single views', title: 'Helmet parts', hand: 'fixed photos and labels — made in GIMP' },
      { key: 'front-spacer', v: 'Page 2 - Front Helmet spacer', title: 'Front helmet spacer', hand: 'fixed photos and labels — made in GIMP' },
      { key: 'back-spacer', v: 'Page 3 - Back Helmet spacer', title: 'Back helmet spacer', hand: 'fixed photos and labels — made in GIMP' },
      { key: 'wearing-1', v: 'Page 4 Instructions for use', title: 'Instructions for wearing (1)', hand: 'fixed instruction text and photos — made in GIMP' },
      { key: 'wearing-2', v: 'Page 5 Instructions for use', title: 'Instructions for wearing (2)', hand: 'fixed instruction text and photos — made in GIMP' },
      { key: 'dos-and-donts', v: "Page 6 - Instructions Do's and Dont's", title: "Do's and don'ts", hand: 'fixed instruction text — made in GIMP' },
    ],
    notPages: {},
  },
  {
    id: 'illustration',
    slug: 'illustration',
    group: 'other',
    label: 'Illustration',
    source: { file: 'Illustration - O3D-2026-XX-XX-00XX_x_ V1.3.4.xcf', md5: '6fbca214cef8ad765e0127cf863336cf' },
    // The same document the Packaging Illustration tool already makes. One way to
    // make it, not two.
    link: { to: '/packaging', label: 'Packaging Illustration' },
    pages: [],
    notPages: {},
  },
]

/** Old ids that still arrive: stored page lists and the orbital golden. */
const ALIASES = { orbital: 'design-confirmation' }

const byId = new Map(FORMATS.map((f) => [f.id, f]))

/** The format for an id (or an alias), or undefined. */
export const formatFor = (id) => byId.get(ALIASES[id] ?? id)

/** The id a stored or legacy id stands for. */
export const canonicalFormatId = (id) => formatFor(id)?.id ?? id

/**
 * The recipe set a document drafts with. `orbital` itself stays the orbital
 * recipes' key (goldens, stored lists); a format names its own set.
 */
export const recipesKeyFor = (id) => (id === 'orbital' ? 'orbital' : formatFor(id)?.recipes ?? null)

/** The colour set a document is painted with. */
export const paletteFor = (id) => (id === 'orbital' ? 'orbital' : formatFor(id)?.palette ?? null)

/**
 * The page geometry for a document's format: THE CHASSIS, for every one of them.
 *
 * Until 26 Sep 2026 each format carried its own measured geometry, derived from its own
 * template, with an interim 8 pt footer override on top. Deepak, 26 Sep: one source of truth —
 * the team's master page, measured into template.js — used "on the GIMP templates and also on
 * the portal now". A format keeps what is its own: its report name (`chrome.titleLine2`), its
 * pages, its recipes, its palette. The chrome is the house's.
 */
export function geometryFor(id) {
  // Unknown ids (a typo in a stored case, a format that was removed) get nothing, so the
  // writer's `if (!g) throw` guards keep their meaning (G3 review, 28 Sep).
  return formatFor(id) ? CHASSIS : undefined
}

/** The report fields a format prints in its header and footer. */
export function chromeFor(id) {
  const c = formatFor(id)?.chrome
  if (!c) return null
  return {
    titleLine1: c.titleLine1?.text ?? '',
    titleLine2: c.titleLine2?.text ?? '',
    ...(c.productName ? { productName: c.productName.text } : {}),
    version: c.version?.text ?? null,
  }
}

/**
 * The formats the picker shows before "Show all formats".
 *
 * The work the team actually delivers, from the tracker (2024-26): fibula guide
 * 234 cases, cranial mould 67, orthognathic wafer 41. Planning images ride along
 * because they are sent during planning on most cases. Everything else is one
 * click away, not gone.
 */
export const COMMON_FORMATS = ['fibula', 'orthognathic', 'cranioplasty', 'planning']

/**
 * Which format the files on a page point at — a suggestion, never a decision.
 *
 * Read from the roles the classifier already assigns, and only where a role says
 * it plainly. Scapula, cranioplasty and Ti PSI parts carry no word the classifier
 * knows, so they are never guessed at: saying nothing is the honest answer.
 *
 * @returns {{id: string, because: string[]} | null}
 */
export function suggestFormat(library = []) {
  const roles = new Set(library.map((m) => m.role))
  const has = (...names) => names.filter((n) => roles.has(n))
  const RULES = [
    ['fibula', ['fibula_segments', 'fibula', 'fibula_guide', 'leg', 'tibia']],
    ['design-confirmation', ['orbital_volume_left', 'orbital_volume_right', 'orbital_difference']],
    ['orthognathic', ['splint_intermediate', 'splint_final', 'splint_movement']],
  ]
  for (const [id, names] of RULES) {
    const found = has(...names)
    if (found.length) return { id, because: found }
  }
  return null
}

/** What a built report is saved as: "<case id>_<format>.pdf". */
export const reportFileName = (id, caseId) =>
  `${String(caseId || 'case').replace(/[^\w-]/g, '')}_${formatFor(id)?.fileSuffix ?? 'case_report'}.pdf`

/** Formats the Builder offers: every one with pages or a link. */
export const buildable = (f) => !f.link && f.pages.some((p) => !p.hand)
